import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";

import { useLazyVmsSearchQuery, VmsSearchConfig } from "@/api/elasticSearch/vmsSearchApi";
import { ResultTypes, SearchResult } from "@/utilities/types/search";


export type UseSearchConfig = Omit<VmsSearchConfig, "offset" | "limit">;

interface SearchHookResults {
    results: SearchResult<ResultTypes>[],
    total: number | undefined,
    search_time: number | undefined,
    error: FetchBaseQueryError | SerializedError | undefined,
    isSuccess: boolean,
    isLoading: boolean,
    isFetching: boolean
}

type SearchFunction = (config:UseSearchConfig) => void;
type MoreFunction = () => void;
type SearchHookReturnType = [ SearchFunction, MoreFunction, SearchHookResults ];

export const useSearch = (perPage: number):SearchHookReturnType => {
    const [ config, setConfig ] = useState<UseSearchConfig|undefined>(undefined);
    const [ searchQuery, hookState ] = useLazyVmsSearchQuery(undefined);
    const [ searchResults, setSearchResults] = useState<SearchResult<ResultTypes>[]>([]);
    const [ searchOffset, setSearchOffset] = useState(0);
    const [ totalResultCount, setTotalResultCount ] = useState<number | undefined>(undefined);
    const [ searchTime, setSearchTime ] = useState<number | undefined>(undefined);
    // const prefetchResults = usePrefetch("vmsSearch");

    const loadMore = () => {
        if(config && searchResults.length < (totalResultCount ?? 0)) {
            setSearchOffset(searchOffset + perPage);

            const pageSearch = { ...config, offset: searchOffset, limit: perPage };

            searchQuery(pageSearch);

            // prefetchResults({ ...pageSearch, offset: searchOffset + perPage });
        }
    };

    const search = (config:UseSearchConfig) => {
        setConfig(config);
        setSearchResults([]);
        searchQuery({ ...config, offset: 0, limit: perPage });
        // prefetchResults({ ...config, offset: perPage, limit: perPage });

        setSearchOffset(perPage);
    };

    useEffect( () => {
        if(hookState.data && !hookState.isFetching) {
            setTotalResultCount(hookState.data.data.meta.count);
            setSearchTime(hookState.data.data.meta.took);
            setSearchResults( [...searchResults, ...hookState.data.data.results]);
        }
    }, [hookState.data, hookState.isFetching]);

    return [
        search,
        loadMore,
        {
            results: searchResults,
            total: totalResultCount,
            search_time: searchTime,
            error: hookState.error,
            isSuccess: hookState.isSuccess,
            isLoading: hookState.isLoading,
            isFetching: hookState.isFetching,
        },
    ];
};
